// frontend/src/auth/mfa-enroll.ts
// MFA 登録ウィザードの土台（TOTP / UV付きパスキー, 設計 §2.2）。
// WCAG2: tablist/tab/aria-selected、label 関連付け、role=alert。
// 注（R5）: クライアントの登録完了表示は UX のみ。MFA 成立はサーバ側（amr/AdminGetUser）が判定する。
import type { AppConfig } from "../config";
import { createCognitoIdp, type CognitoIdp } from "./cognito-idp";
import { createCognitoMfa, type CognitoMfa } from "./cognito-idp-mfa";

export interface MfaEnrollDeps {
  readonly config: AppConfig;
  readonly getAccessToken: () => string | null;
  readonly onDone: () => void;
  readonly mfa?: CognitoMfa;
  readonly createCredential?: (
    options: CredentialCreationOptions,
  ) => Promise<Credential | null>;
  /** MFA_SETUP フロー: initiate-auth から渡された Session */
  readonly session?: string;
  /** MFA_SETUP フロー: ログイン時のユーザー名 */
  readonly username?: string;
  /** MFA_SETUP フロー: RespondToAuthChallenge に使う IdP クライアント */
  readonly idp?: CognitoIdp;
  /** MFA_SETUP フロー完了後のコールバック(AccessToken を受け取る) */
  readonly onAuthenticated?: (token: string) => void;
}

type Method = "totp" | "passkey";

export function renderMfaEnroll(root: HTMLElement, deps: MfaEnrollDeps): void {
  const mfa = deps.mfa ?? createCognitoMfa(deps.config.region);
  const createCredential =
    deps.createCredential ?? ((options) => navigator.credentials.create(options));
  root.replaceChildren();

  // session がある場合は MFA_SETUP フロー(AccessToken 未取得)。ない場合は通常の accessToken フロー。
  const sessionMode = typeof deps.session === "string" && deps.session !== "";

  const accessToken = deps.getAccessToken();
  if (!sessionMode && (accessToken === null || accessToken === "")) {
    const err = document.createElement("p");
    err.setAttribute("role", "alert");
    err.className = "text-sm text-red-700";
    err.textContent = "セッションが切れています。再度ログインしてください。";
    root.append(err);
    return;
  }

  const heading = document.createElement("h1");
  heading.className = "text-xl font-semibold text-slate-900";
  heading.textContent = "多要素認証の登録";

  const alert = document.createElement("div");
  alert.setAttribute("role", "alert");
  alert.setAttribute("aria-live", "assertive");
  alert.className = "min-h-[1.25rem] text-sm text-red-700";

  const tablist = document.createElement("div");
  tablist.setAttribute("role", "tablist");
  tablist.className = "flex gap-2 border-b border-slate-200";

  const panel = document.createElement("div");
  panel.className = "mt-4";

  const totpTab = makeTab("totp", "認証アプリ（TOTP）");
  const passkeyTab = makeTab("passkey", "パスキー");
  tablist.append(totpTab, passkeyTab);
  root.append(heading, alert, tablist, panel);

  function showError(message: string): void {
    alert.textContent = message;
  }

  function select(method: Method): void {
    for (const tab of [totpTab, passkeyTab]) {
      const selected = tab.dataset["method"] === method;
      tab.setAttribute("aria-selected", String(selected));
      tab.className = selected
        ? "border-b-2 border-shinonome-700 px-3 py-2 font-medium text-shinonome-800"
        : "px-3 py-2 text-slate-600";
    }
    alert.textContent = "";
    if (method === "totp") {
      renderTotp();
    } else {
      renderPasskey();
    }
  }

  function renderTotp(): void {
    panel.replaceChildren();
    const secret = document.createElement("p");
    secret.id = "totp-secret";
    secret.className = "break-all rounded bg-slate-50 p-3 font-mono text-sm text-slate-800";
    secret.textContent = "シードを取得しています...";

    const label = document.createElement("label");
    label.htmlFor = "totp-code";
    label.className = "mt-4 block text-sm font-medium text-slate-700";
    label.textContent = "認証アプリのコード（6桁）";

    const input = document.createElement("input");
    input.id = "totp-code";
    input.type = "text";
    input.inputMode = "numeric";
    input.autocomplete = "one-time-code";
    input.className =
      "mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-slate-900 focus:border-shinonome-700";

    const verify = document.createElement("button");
    verify.id = "totp-verify";
    verify.type = "button";
    verify.className =
      "mt-3 w-full rounded bg-shinonome-700 px-4 py-2 font-medium text-white hover:bg-shinonome-800 disabled:opacity-60";
    verify.textContent = "検証して有効化";

    panel.append(secret, label, input, verify);

    if (sessionMode) {
      // MFA_SETUP フロー: Session ベース
      const sessionVal = deps.session as string;
      const idpClient = deps.idp ?? createCognitoIdp(deps.config.region);
      let currentSession = sessionVal;

      mfa
        .associateSoftwareToken({ session: sessionVal })
        .then((res) => {
          secret.textContent = `シークレットキー: ${res.SecretCode ?? ""}`;
          if (typeof res.Session === "string" && res.Session !== "") {
            currentSession = res.Session;
          }
        })
        .catch(() => showError("シードの取得に失敗しました。"));

      verify.addEventListener("click", () => {
        verify.disabled = true;
        mfa
          .verifySoftwareToken({ session: currentSession, userCode: input.value.trim() })
          .then((res) => {
            if (res.Status !== "SUCCESS") {
              throw new Error("VerifyFailed");
            }
            const newSession = typeof res.Session === "string" && res.Session !== "" ? res.Session : currentSession;
            return idpClient.respondToAuthChallenge({
              clientId: deps.config.spaClientId,
              challengeName: "MFA_SETUP",
              session: newSession,
              challengeResponses: { USERNAME: deps.username ?? "" },
            });
          })
          .then((res) => {
            const token = res.AuthenticationResult?.AccessToken;
            if (typeof token !== "string" || token === "") {
              throw new Error("NoAccessToken");
            }
            // MFA を enabled+preferred に記録（UserMFASettingList を populate）。これが無いと、
            // TOTP 検証は通っても UserMFASettingList が空のままになり、authorizer の MFA 粗ゲート
            // （amr 不在→ AdminGetUser フォールバック）が「MFA 未完」と判定して全 API を 403 にする。
            return mfa.setUserMfaPreference({ accessToken: token }).then(() => {
              if (deps.onAuthenticated !== undefined) {
                deps.onAuthenticated(token);
              }
            });
          })
          .catch(() => showError("コードの検証に失敗しました。"))
          .finally(() => {
            verify.disabled = false;
          });
      });
    } else {
      // 通常フロー: AccessToken ベース
      mfa
        .associateSoftwareToken({ accessToken: accessToken as string })
        .then((res) => {
          secret.textContent = `シークレットキー: ${res.SecretCode ?? ""}`;
        })
        .catch(() => showError("シードの取得に失敗しました。"));

      verify.addEventListener("click", () => {
        verify.disabled = true;
        mfa
          .verifySoftwareToken({ accessToken: accessToken as string, userCode: input.value.trim() })
          .then((res) => {
            if (res.Status !== "SUCCESS") {
              throw new Error("VerifyFailed");
            }
            return mfa.setUserMfaPreference({ accessToken: accessToken as string });
          })
          .then(() => deps.onDone())
          .catch(() => showError("コードの検証に失敗しました。"))
          .finally(() => {
            verify.disabled = false;
          });
      });
    }
  }

  function renderPasskey(): void {
    panel.replaceChildren();
    const note = document.createElement("p");
    note.className = "text-sm text-slate-700";

    if (sessionMode) {
      // MFA_SETUP フロー: AccessToken が無いためパスキー登録は不可。
      note.textContent =
        "初回登録は認証アプリ（TOTP）をご利用ください。パスキーはログイン後に追加できます。";
      panel.append(note);
      return;
    }

    note.textContent = "ユーザー検証（UV）付きのパスキーを登録します。";

    const register = document.createElement("button");
    register.id = "passkey-register";
    register.type = "button";
    register.className =
      "mt-3 w-full rounded bg-shinonome-700 px-4 py-2 font-medium text-white hover:bg-shinonome-800 disabled:opacity-60";
    register.textContent = "パスキーを登録";

    panel.append(note, register);

    register.addEventListener("click", () => {
      register.disabled = true;
      mfa
        .startWebAuthnRegistration({ accessToken: accessToken as string })
        .then((res) => {
          const options = res.CredentialCreationOptions as unknown as CredentialCreationOptions;
          // UV 必須を強制（R15: UV なしでの MFA 格下げを防ぐ）。
          const publicKey = (options.publicKey ?? {}) as PublicKeyCredentialCreationOptions;
          publicKey.authenticatorSelection = {
            ...(publicKey.authenticatorSelection ?? {}),
            userVerification: "required",
          };
          options.publicKey = publicKey;
          return createCredential(options);
        })
        .then((credential) => {
          if (credential === null) {
            throw new Error("CredentialCancelled");
          }
          return mfa.completeWebAuthnRegistration({
            accessToken: accessToken as string,
            credential,
          });
        })
        .then(() => deps.onDone())
        .catch(() => showError("パスキーの登録に失敗しました。"))
        .finally(() => {
          register.disabled = false;
        });
    });
  }

  function makeTab(method: Method, text: string): HTMLButtonElement {
    const tab = document.createElement("button");
    tab.type = "button";
    tab.setAttribute("role", "tab");
    tab.dataset["method"] = method;
    tab.setAttribute("aria-selected", "false");
    tab.className = "px-3 py-2 text-slate-600";
    tab.textContent = text;
    tab.addEventListener("click", () => select(method));
    return tab;
  }
}
